import { Link } from "react-router-dom";
import Footer from "../components/Footer";

export default function Help() {
  return (
    <div className="min-h-screen bg-white">
      {/* Modern Navigation */}
      <nav className="fixed top-0 w-full z-50 bg-white/80 backdrop-blur-md border-b border-gray-200/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h4" />
                </svg>
              </div>
              <span className="text-xl font-bold text-gray-900 tracking-tight">Prindox</span>
            </Link>
            <Link
              to="/"
              className="text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors"
            >
              Back to Home
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gray-50 border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-600 rounded-2xl mb-6 text-white shadow-lg shadow-blue-200">
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z" />
            </svg>
          </div>
          <h1 className="text-4xl md:text-5xl font-black text-gray-900 mb-6 tracking-tight">Help Center</h1>
          <p className="text-xl text-gray-600 leading-relaxed max-w-2xl mx-auto">
            Find answers to common questions and get help using Prindox.
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Quick Start Guide */}
          <div className="mb-20">
            <h2 className="text-3xl font-bold text-gray-900 mb-10 text-center">Get Started Fast</h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-white p-8 rounded-2xl border border-blue-100 shadow-sm hover:shadow-md transition-shadow relative overflow-hidden">
                <div className="absolute top-0 right-0 w-32 h-32 bg-blue-50 rounded-bl-full -mr-10 -mt-10 z-0"></div>
                <div className="relative z-10">
                  <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
                    <span className="w-8 h-8 bg-blue-600 text-white rounded-lg flex items-center justify-center text-sm mr-3">1</span>
                    For Users
                  </h3>
                  <ol className="space-y-4 mb-8">
                    {[
                      "Find a nearby print shop with a Prindox QR code",
                      "Open your phone camera and scan the QR code",
                      "Select and upload your documents securely",
                      "Wait for the 'ready for pickup' notification",
                      "Collect your printed documents and confirm"
                    ].map((step, i) => (
                      <li key={i} className="flex items-start text-gray-600">
                        <span className="mr-3 text-blue-400 mt-1">•</span>
                        {step}
                      </li>
                    ))}
                  </ol>
                  <Link to="/send" className="inline-flex items-center text-blue-600 font-bold hover:text-blue-700">
                    Send a Document
                    <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                    </svg>
                  </Link>
                </div>
              </div>
              
              <div className="bg-white p-8 rounded-2xl border border-green-100 shadow-sm hover:shadow-md transition-shadow relative overflow-hidden">
                <div className="absolute top-0 right-0 w-32 h-32 bg-green-50 rounded-bl-full -mr-10 -mt-10 z-0"></div>
                <div className="relative z-10">
                  <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
                    <span className="w-8 h-8 bg-green-600 text-white rounded-lg flex items-center justify-center text-sm mr-3">2</span>
                    For Shop Owners
                  </h3>
                  <ol className="space-y-4 mb-8">
                    {[
                      "Register your print shop account in seconds",
                      "Set up your shop details, prices, and hours",
                      "Generate and display your unique QR code",
                      "Receive document notifications instantly",
                      "Print documents and delight customers"
                    ].map((step, i) => (
                      <li key={i} className="flex items-start text-gray-600">
                        <span className="mr-3 text-green-400 mt-1">•</span>
                        {step}
                      </li>
                    ))}
                  </ol>
                  <Link to="/register" className="inline-flex items-center text-green-600 font-bold hover:text-green-700">
                    Register Shop
                    <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                    </svg>
                  </Link>
                </div>
              </div>
            </div>
          </div>

          {/* FAQ Section */}
          <div className="mb-20">
            <h2 className="text-3xl font-bold text-gray-900 mb-10 text-center">Frequently Asked Questions</h2>
            <div className="grid md:grid-cols-2 gap-6">
              
              <div className="space-y-6">
                <div className="bg-gray-50 p-6 rounded-xl">
                  <h4 className="font-bold text-gray-900 mb-2">Do I need to install an app?</h4>
                  <p className="text-gray-600 text-sm leading-relaxed">No! Prindox works entirely through your web browser. Just scan the QR code with your phone's camera.</p>
                </div>
                <div className="bg-gray-50 p-6 rounded-xl">
                  <h4 className="font-bold text-gray-900 mb-2">Is my data secure?</h4>
                  <p className="text-gray-600 text-sm leading-relaxed">Yes. All documents are encrypted during transfer and automatically deleted after printing confirmation.</p>
                </div>
                <div className="bg-gray-50 p-6 rounded-xl">
                  <h4 className="font-bold text-gray-900 mb-2">What file types are supported?</h4>
                  <p className="text-gray-600 text-sm leading-relaxed">We support PDF, Word, PowerPoint, Excel, JPG, PNG, and 20+ other common formats.</p>
                </div>
              </div>

              <div className="space-y-6">
                <div className="bg-gray-50 p-6 rounded-xl">
                  <h4 className="font-bold text-gray-900 mb-2">How much does printing cost?</h4>
                  <p className="text-gray-600 text-sm leading-relaxed">Prices are set by individual print shops. You'll see the exact cost before confirming.</p>
                </div>
                <div className="bg-gray-50 p-6 rounded-xl">
                  <h4 className="font-bold text-gray-900 mb-2">Can I upload multiple files?</h4>
                  <p className="text-gray-600 text-sm leading-relaxed">Yes! You can select and upload multiple files in a single batch for efficient printing.</p>
                </div>
                <div className="bg-gray-50 p-6 rounded-xl">
                  <h4 className="font-bold text-gray-900 mb-2">How do I join as a shop?</h4>
                  <p className="text-gray-600 text-sm leading-relaxed">Simply register an account, verify your details, and you'll get your unique QR code instantly.</p>
                </div>
              </div>

            </div>
          </div>

          {/* Contact Support */}
          <div className="bg-slate-900 rounded-3xl p-12 text-center text-white relative overflow-hidden">
            <div className="absolute top-0 left-0 w-full h-full bg-blue-600 opacity-10 mix-blend-overlay"></div>
            <h2 className="text-3xl font-bold mb-6 relative z-10">Still Need Help?</h2>
            <p className="text-gray-300 mb-10 max-w-lg mx-auto relative z-10">
              Can't find what you're looking for? Our support team is here to help.
            </p>
            
            <div className="grid md:grid-cols-3 gap-6 max-w-3xl mx-auto relative z-10">
              <div className="bg-white/5 backdrop-blur-sm p-6 rounded-2xl border border-white/10 hover:bg-white/10 transition-colors">
                <div className="text-2xl mb-3">📧</div>
                <h3 className="font-bold text-white mb-1">Email Support</h3>
                <p className="text-blue-300 text-sm mb-2">support@scan2print.com</p>
                <p className="text-gray-500 text-xs">Response in 24h</p>
              </div>
              
              <div className="bg-white/5 backdrop-blur-sm p-6 rounded-2xl border border-white/10 hover:bg-white/10 transition-colors">
                <div className="text-2xl mb-3">💬</div>
                <h3 className="font-bold text-white mb-1">Live Chat</h3>
                <p className="text-blue-300 text-sm mb-2">Available on website</p>
                <p className="text-gray-500 text-xs">Mon-Fri 9AM-6PM</p>
              </div>
              
              <div className="bg-white/5 backdrop-blur-sm p-6 rounded-2xl border border-white/10 hover:bg-white/10 transition-colors">
                <div className="text-2xl mb-3">📺</div>
                <h3 className="font-bold text-white mb-1">Video Guides</h3>
                <p className="text-blue-300 text-sm mb-2">Step-by-step tutorials</p>
                <p className="text-gray-500 text-xs">Coming soon</p>
              </div>
            </div>
                    </div>
                  </div>
                </section>
                <Footer />
              </div>
            );
          }